import { CreditCard, Landmark, MailPlus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import { type PaymentType as PayableType } from "@/api-v3";
import { Button } from "@/components/ui/button";
import { getCardIcon } from "@/features/card";
import {
  getCardTypeForAccountType,
  setReturnTo,
  startClaimDetour,
} from "@/features/payment-methods";
import { describePaymentAccount } from "@/features/payment-methods/helpers/payment-account";
import {
  type PaymentAccount as PaymentAccountData,
  type PaymentAccountTargetType,
} from "@/features/payments/types/payment-account";
import { useAppDispatch } from "@/store";
import { AppRoute } from "@/types/routes";

import { canReceivePayableType } from "../helpers/allowed-payable-types";

const paymentMethodIcon = (type: PaymentAccountTargetType) => {
  if (type === "ACH_OUT" || type === "BANK_ACCOUNT") {
    return <Landmark className="h-6 w-6" />;
  }

  // Card accounts take the icon their Card Type already owns, so a Stipend Card
  // gets the credit card and a Spend Card the wallet rather than the envelope.
  const cardType = getCardTypeForAccountType(type);
  if (cardType) {
    const CardIcon = getCardIcon(cardType);
    return <CardIcon className="h-6 w-6" />;
  }

  if (type === "DEBIT_CARD") {
    return <CreditCard className="h-6 w-6" />;
  }

  return <MailPlus className="h-6 w-6" />;
};

interface PaymentAccountProps {
  paymentAccount: PaymentAccountData;
  showChangeButton?: boolean;
  /**
   * The Payable Item kind this account has to be able to receive. Set it where
   * the account is shown as part of a Claim, so that changing an account which
   * cannot receive that kind starts a Claim Detour rather than an open-ended
   * visit to the list: the list then says why and offers only what can be paid.
   */
  requiredPayableType?: PayableType;
}

export const PaymentAccount = ({
  paymentAccount,
  showChangeButton = true,
  requiredPayableType,
}: PaymentAccountProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const onProceed = () => {
    // Only a refusal is a detour. An account that can already receive this kind
    // is being changed by choice, and must not be met with a detour notice.
    const needsClaimCapableAccount =
      requiredPayableType !== undefined &&
      !canReceivePayableType(paymentAccount, requiredPayableType);

    dispatch(
      needsClaimCapableAccount
        ? startClaimDetour({
            returnTo: location.pathname,
            requiredPayableType,
          })
        : setReturnTo(location.pathname)
    );
    navigate(AppRoute.PAYMENT_METHODS_LIST);
  };

  return (
    <div
      data-test-id="payment-account"
      className="flex flex-row gap-4 bg-background rounded-lg border p-4"
    >
      <div className="text-muted-foreground">
        {paymentMethodIcon(paymentAccount.target_account_type)}
      </div>
      <div className="flex-1">
        <h6 className="text-muted-foreground text-sm leading-relaxed font-semibold">
          {t("mileageReview_PaymentMethod")}
        </h6>

        <p className="text-sm leading-relaxed">
          {describePaymentAccount({
            accountType: paymentAccount.target_account_type,
            identifier: paymentAccount.target_account_identifier,
            title: paymentAccount.target_account_title,
            t,
          })}
        </p>

        {showChangeButton && (
          <Button
            onClick={onProceed}
            variant="link"
            size="sm"
            className="capitalize underline p-0 font-normal h-auto"
          >
            {t("moneyPickup_selectPayment_secondaryButton")}
          </Button>
        )}
      </div>
    </div>
  );
};
